import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Search, Calendar, Phone, Mail, FileText, CheckSquare, AlertCircle, ChevronLeft, ChevronRight, Users, BarChart3, Eye } from "lucide-react";
import { Card, CardHeader, CardContent } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Header } from "../layout/Header";
import { AuthContext } from "../contexts/AuthContext";

const ENGAGEMENT_TYPE_ICONS = {
  MEETING: Calendar,
  CALL: Phone,
  EMAIL: Mail,
  NOTE: FileText,
  TASK: CheckSquare,
};

const ENGAGEMENT_TYPE_COLORS = {
  MEETING: "warning",
  CALL: "default",
  EMAIL: "success",
  NOTE: "secondary",
  TASK: "outline",
};

export default function ManageEngagementsPage() {
  const { token } = useContext(AuthContext);
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterCustomerId, setFilterCustomerId] = useState("");
  const [filterContactId, setFilterContactId] = useState("");
  const [sortBy, setSortBy] = useState("date-desc");
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0,
    has_more: false
  });
  const [selectedEngagement, setSelectedEngagement] = useState(null);
  const [contactsWithNotes, setContactsWithNotes] = useState([]);
  const [engagementsWithNotes, setEngagementsWithNotes] = useState(new Map());
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [loadingContactNotes, setLoadingContactNotes] = useState(false);
  const [engagementStats, setEngagementStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // Fetch engagements when filters change
  useEffect(() => {
    fetchEngagements();
  }, [filterType, filterCustomerId, filterContactId, pagination.limit, pagination.offset]);

  // Fetch stats on initial load
  useEffect(() => {
    fetchEngagementStats();
  }, []);

  const fetchEngagements = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset
      });

      if (filterType !== "all") {
        params.append("engagement_type", filterType);
      }
      if (filterCustomerId) {
        params.append("customer_id", filterCustomerId);
      }
      if (filterContactId) {
        params.append("contact_id", filterContactId);
      }

      const response = await axios.get(`/engagements?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const engagementsData = response.data.engagements || [];
      setEngagements(engagementsData);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        has_more: response.data.has_more || false
      }));

      // Pre-fetch contact notes for all engagements
      await fetchContactNotesForEngagements(engagementsData);
    } catch (err) {
      console.error("Error fetching engagements:", err);
      setError(err.response?.data?.detail || err.message || "Failed to fetch engagements");
    } finally {
      setLoading(false);
    }
  };

  const fetchEngagementStats = async () => {
    try {
      setLoadingStats(true);
      const response = await axios.get("/engagements/stats/summary", {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setEngagementStats(response.data);
    } catch (err) {
      console.error("Error fetching engagement stats:", err);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchContactNotesForEngagements = async (engagementsData) => {
    try {
      const notesMap = new Map();
      
      // Group engagements by customer_id to minimize API calls
      const customerGroups = engagementsData.reduce((acc, engagement) => {
        if (engagement.customer_id) {
          if (!acc[engagement.customer_id]) {
            acc[engagement.customer_id] = [];
          }
          acc[engagement.customer_id].push(engagement);
        }
        return acc;
      }, {});

      // Fetch contacts for each customer
      await Promise.all(
        Object.entries(customerGroups).map(async ([customerId, customerEngagements]) => {
          try {
            const contactsResponse = await axios.get(`/customers/${customerId}/complete-profile`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            const contacts = contactsResponse.data.contacts || [];
            
            // Process each engagement for this customer
            customerEngagements.forEach(engagement => {
              if (engagement.contact_ids?.length > 0) {
                const engagementContacts = engagement.contact_ids.map(contactId => {
                  const contact = contacts.find(c => c.hubspot_contact_id === contactId);
                  return contact || {
                    hubspot_contact_id: contactId,
                    name: `Contact ${contactId}`,
                    bm_notes: null,
                    rsm_notes: null,
                    notes: null
                  };
                });
                
                notesMap.set(engagement.id, engagementContacts);
              }
            });
          } catch (contactErr) {
            console.error(`Failed to fetch contacts for customer ${customerId}:`, contactErr);
          }
        })
      );

      setEngagementsWithNotes(notesMap);
    } catch (err) {
      console.error("Error fetching contact notes for engagements:", err);
    }
  };

  const fetchEngagementDetails = async (engagementId) => {
    try {
      setLoadingContactNotes(true);
      
      // Get engagement details
      const response = await axios.get(`/engagements/${engagementId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const engagement = response.data.engagement;
      setSelectedEngagement(engagement);

      // Check if we already have contact notes pre-fetched
      const preloadedContacts = engagementsWithNotes.get(engagementId);
      
      if (preloadedContacts) {
        // Use pre-fetched contact notes
        setContactsWithNotes(preloadedContacts);
      } else if (engagement.customer_id && engagement.contact_ids?.length > 0) {
        // Fall back to fetching contact notes if not pre-loaded
        try {
          const contactsResponse = await axios.get(`/customers/${engagement.customer_id}/complete-profile`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          // Match contacts by HubSpot ID and enrich with notes
          const enrichedContacts = engagement.contact_ids.map(contactId => {
            const contact = contactsResponse.data.contacts.find(c => c.hubspot_contact_id === contactId);
            return contact || {
              hubspot_contact_id: contactId,
              name: `Contact ${contactId}`,
              bm_notes: null,
              rsm_notes: null,
              notes: null
            };
          });

          setContactsWithNotes(enrichedContacts);
          
          // Cache the result for future use
          setEngagementsWithNotes(prev => new Map(prev.set(engagementId, enrichedContacts)));
        } catch (contactErr) {
          console.error("Error fetching contact notes:", contactErr);
          // Set basic contact info without notes
          setContactsWithNotes(engagement.contact_ids.map(contactId => ({
            hubspot_contact_id: contactId,
            name: `Contact ${contactId}`,
            bm_notes: null,
            rsm_notes: null,
            notes: null
          })));
        }
      } else {
        setContactsWithNotes([]);
      }
      
      setDetailsModalOpen(true);
    } catch (err) {
      console.error("Error fetching engagement details:", err);
      alert("Failed to fetch engagement details");
    } finally {
      setLoadingContactNotes(false);
    }
  };

  // Filter engagements locally by search term
  const filteredEngagements = engagements
    .filter((engagement) => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesTitle = engagement.title?.toLowerCase().includes(searchLower);
        const matchesBody = engagement.body?.toLowerCase().includes(searchLower);
        const matchesCustomer = engagement.customer_name?.toLowerCase().includes(searchLower);
        const matchesContacts = engagement.contact_names?.some(name => 
          name.toLowerCase().includes(searchLower)
        );
        const matchesCreator = engagement.created_by?.toLowerCase().includes(searchLower);
        
        return matchesTitle || matchesBody || matchesCustomer || matchesContacts || matchesCreator;
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "date-desc":
          return new Date(b.created_at) - new Date(a.created_at);
        case "date-asc":
          return new Date(a.created_at) - new Date(b.created_at);
        case "type":
          return a.type.localeCompare(b.type);
        case "customer":
          return (a.customer_name || "").localeCompare(b.customer_name || "");
        default:
          return 0;
      }
    });

  const getEngagementIcon = (type) => {
    const Icon = ENGAGEMENT_TYPE_ICONS[type] || FileText;
    return <Icon className="h-4 w-4" />;
  };

  const handlePageChange = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterType("all");
    setFilterCustomerId("");
    setFilterContactId("");
    setSortBy("date-desc");
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  // Display actual BM/RSM notes summary in the main table
  const getNoteSummary = (engagement) => {
    const contacts = engagementsWithNotes.get(engagement.id) || [];
    
    if (contacts.length === 0) {
      return "No contacts";
    }

    const bmNotes = contacts.filter(c => c.bm_notes?.trim()).length;
    const rsmNotes = contacts.filter(c => c.rsm_notes?.trim()).length;
    const generalNotes = contacts.filter(c => c.notes?.trim()).length;

    const parts = [];
    if (bmNotes > 0) parts.push(`${bmNotes} BM`);
    if (rsmNotes > 0) parts.push(`${rsmNotes} RSM`);
    if (generalNotes > 0) parts.push(`${generalNotes} General`);

    if (parts.length === 0) {
      return "No notes";
    }

    return parts.join(", ");
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading engagements...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Header />
        <div className="container mx-auto px-4 py-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Error loading engagements: {error}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Manage Engagements</h1>
            <p className="text-muted-foreground mt-1">
              View and manage all engagements across itineraries
            </p>
          </div>
        </div>

        {/* Filters and Search */}
        <Card>
          <CardHeader className="pb-4">
            <h2 className="text-base font-semibold">Filters</h2>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search engagements..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="MEETING">Meetings</SelectItem>
                  <SelectItem value="CALL">Calls</SelectItem>
                  <SelectItem value="EMAIL">Emails</SelectItem>
                  <SelectItem value="NOTE">Notes</SelectItem>
                  <SelectItem value="TASK">Tasks</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date-desc">Newest First</SelectItem>
                  <SelectItem value="date-asc">Oldest First</SelectItem>
                  <SelectItem value="type">By Type</SelectItem>
                  <SelectItem value="customer">By Customer ID</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            Showing {filteredEngagements.length} of {engagements.length} engagements
          </p>
        </div>

        {/* Engagements Table */}
        <Card>
          <CardContent className="p-0">
            {filteredEngagements.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {searchTerm || filterType !== "all" 
                  ? "No engagements found matching your filters." 
                  : "No engagements recorded yet."}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Contacts</TableHead>
                    <TableHead>BM/RSM Notes</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEngagements.map((engagement) => (
                    <TableRow key={engagement.id} className="hover:bg-muted/50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getEngagementIcon(engagement.type)}
                          <Badge variant={ENGAGEMENT_TYPE_COLORS[engagement.type] || "secondary"}>
                            {engagement.type}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-sm line-clamp-2">
                          {engagement.title || "Untitled"}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">
                            {engagement.customer_name || "Unknown Customer"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            ID: {engagement.customer_id || "N/A"}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm line-clamp-2">
                          {engagement.contact_names?.length > 0 
                            ? engagement.contact_names.join(", ")
                            : "-"}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs space-y-1">
                          {(() => {
                            const contacts = engagementsWithNotes.get(engagement.id) || [];
                            const bmNotes = contacts.filter(c => c.bm_notes?.trim()).length;
                            const rsmNotes = contacts.filter(c => c.rsm_notes?.trim()).length;
                            const generalNotes = contacts.filter(c => c.notes?.trim()).length;

                            if (bmNotes === 0 && rsmNotes === 0 && generalNotes === 0) {
                              return <span className="text-muted-foreground italic">No notes</span>;
                            }

                            return (
                              <div className="flex flex-wrap gap-1">
                                {bmNotes > 0 && (
                                  <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs">
                                    {bmNotes} BM
                                  </span>
                                )}
                                {rsmNotes > 0 && (
                                  <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-xs">
                                    {rsmNotes} RSM
                                  </span>
                                )}
                                {generalNotes > 0 && (
                                  <span className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-xs">
                                    {generalNotes} Gen
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">
                            {format(new Date(engagement.created_at), "MMM d, yyyy")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(engagement.created_at), "h:mm a")}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{engagement.created_by || "-"}</p>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchEngagementDetails(engagement.id)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Engagement Detail Modal */}
        <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedEngagement?.title || "Engagement Details"}
              </DialogTitle>
            </DialogHeader>
            {selectedEngagement && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Type</p>
                    <div className="flex items-center gap-2 mt-1">
                      {getEngagementIcon(selectedEngagement.type)}
                      <Badge variant={ENGAGEMENT_TYPE_COLORS[selectedEngagement.type] || "secondary"}>
                        {selectedEngagement.type}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Created By</p>
                    <p className="text-sm mt-1">{selectedEngagement.created_by || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Customer</p>
                    <p className="text-sm mt-1">
                      {selectedEngagement.customer_name || "Unknown"}
                      {selectedEngagement.customer_id && (
                        <span className="text-muted-foreground ml-1">
                          ({selectedEngagement.customer_id})
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Date</p>
                    <p className="text-sm mt-1">
                      {format(new Date(selectedEngagement.created_at), "PPP p")}
                    </p>
                  </div>
                </div>

                {selectedEngagement.start_time && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Meeting Time</p>
                    <p className="text-sm mt-1">
                      {format(new Date(selectedEngagement.start_time), "PPP p")} - 
                      {format(new Date(selectedEngagement.end_time), "p")}
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-sm font-medium text-muted-foreground">Engagement Notes</p>
                  <div className="mt-1 p-3 bg-muted rounded-md">
                    <p className="text-sm whitespace-pre-wrap">
                      {selectedEngagement.body || "No engagement notes available"}
                    </p>
                  </div>
                </div>

                {/* Contact Notes Section */}
                {contactsWithNotes.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-3">Contacts & Notes</p>
                    {loadingContactNotes ? (
                      <div className="flex items-center justify-center p-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        <span className="ml-2 text-sm text-muted-foreground">Loading contact notes...</span>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {contactsWithNotes.map(contact => (
                          <div key={contact.hubspot_contact_id} className="border rounded-lg p-3 bg-background">
                            <h4 className="font-medium text-sm mb-2">
                              {contact.name}
                              <span className="text-xs text-muted-foreground ml-1">
                                (ID: {contact.hubspot_contact_id})
                              </span>
                            </h4>

                            <div className="space-y-2">
                              {contact.bm_notes && (
                                <div className="bg-blue-50 p-2 rounded">
                                  <p className="text-xs font-medium text-blue-700">Business Manager Notes:</p>
                                  <p className="text-sm text-blue-800 whitespace-pre-wrap">
                                    {contact.bm_notes}
                                  </p>
                                </div>
                              )}

                              {contact.rsm_notes && (
                                <div className="bg-green-50 p-2 rounded">
                                  <p className="text-xs font-medium text-green-700">RSM Notes:</p>
                                  <p className="text-sm text-green-800 whitespace-pre-wrap">
                                    {contact.rsm_notes}
                                  </p>
                                </div>
                              )}

                              {contact.notes && (
                                <div className="bg-gray-50 p-2 rounded">
                                  <p className="text-xs font-medium text-gray-700">General Notes:</p>
                                  <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                    {contact.notes}
                                  </p>
                                </div>
                              )}

                              {!contact.bm_notes && !contact.rsm_notes && !contact.notes && (
                                <p className="text-xs text-muted-foreground italic">
                                  No notes available for this contact
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {contactsWithNotes.length === 0 && !loadingContactNotes && selectedEngagement.contact_names?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Contacts</p>
                    <p className="text-sm mt-1">
                      {selectedEngagement.contact_names.join(", ")}
                    </p>
                    <p className="text-xs text-muted-foreground italic mt-1">
                      Contact notes could not be loaded
                    </p>
                  </div>
                )}

                <div className="text-xs text-muted-foreground">
                  <p>Source: {selectedEngagement.source || "hubspot"}</p>
                  <p>Engagement ID: {selectedEngagement.id}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}